import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DealerRatingSchema, DealerRatingFormValues } from '@/types/dealerRating';
import { useSupabaseAuth } from '@/contexts/auth/SupabaseAuthContext';
import { createDealerRating, checkExistingRating } from '@/services/dealerRatingService';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';

interface UseDealerRatingProps {
  dealerId: string;
  onSuccess?: () => void;
}

export const useDealerRating = ({ dealerId, onSuccess }: UseDealerRatingProps) => {
  const { user, isAuthenticated } = useSupabaseAuth();
  const queryClient = useQueryClient();
  const [hasExistingRating, setHasExistingRating] = useState(false);
  const [isCheckingRating, setIsCheckingRating] = useState(true);

  const form = useForm<DealerRatingFormValues>({
    resolver: zodResolver(DealerRatingSchema),
    defaultValues: {
      rating: 0,
      comment: '',
      dealer_id: dealerId,
      user_id: user?.id || '',
    },
  });

  useEffect(() => {
    if (user?.id && dealerId) {
      form.setValue('user_id', user.id);
      form.setValue('dealer_id', dealerId);

      const checkRating = async () => {
        setIsCheckingRating(true);
        try {
          const exists = await checkExistingRating(dealerId, user.id);
          setHasExistingRating(exists);
        } catch (error) {
          toast.error("Errore nel verificare la recensione esistente.");
        } finally {
          setIsCheckingRating(false);
        }
      };
      checkRating();
    } else {
        setIsCheckingRating(false);
    }
  }, [dealerId, user?.id, form]);

  const onSubmit = async (values: DealerRatingFormValues) => {
    console.log('[DEBUG] Starting rating submission:', {
      values,
      isAuthenticated,
      user: user ? { id: user.id, email: user.email } : null,
      hasExistingRating,
      dealerId
    });

    if (!isAuthenticated || !user) {
      console.log('[DEBUG] Authentication failed');
      toast.error("Devi essere autenticato per lasciare una recensione.");
      return;
    }
    
    if (hasExistingRating) {
      console.log('[DEBUG] User already has rating for this dealer');
      toast.error("Hai già lasciato una recensione per questo compro oro.");
      return;
    }

    try {
      // Validazione preliminare
      if (!values.rating || values.rating < 1 || values.rating > 5) {
        throw new Error("Rating non valido");
      }
      
      if (!values.dealer_id) {
        throw new Error("ID dealer mancante");
      }

      console.log('[DEBUG] Calling createDealerRating with:', values);
      await createDealerRating(values);
      
      console.log('[DEBUG] Rating created successfully');
      toast.success("Recensione inviata con successo!");
      
      // Invalidate queries to refetch ratings and dealer details (avg rating)
      await queryClient.invalidateQueries({ queryKey: ['dealerRatings', dealerId] });
      await queryClient.invalidateQueries({ queryKey: ['dealerDetail', dealerId] });

      if (onSuccess) {
        console.log('[DEBUG] Calling onSuccess callback');
        onSuccess();
      }
      form.reset();
    } catch (error: any) {
      console.error('[useDealerRating] Detailed error:', {
        error,
        message: error.message,
        stack: error.stack,
        values
      });
      
      // Messaggi di errore più specifici
      if (error.message.includes('already rated')) {
        toast.error("Hai già lasciato una recensione per questo compro oro.");
      } else if (error.message.includes('authenticated')) {
        toast.error("Devi essere autenticato per lasciare una recensione.");
      } else {
        toast.error(error.message || "Si è verificato un errore durante l'invio della recensione.");
      }
    }
  };

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isLoading: form.formState.isSubmitting || isCheckingRating,
    hasExistingRating,
  };
};
